import axios from 'axios'
import jwt_decode from 'jwt-decode'
import { handleApiError } from '@/utils/apiHelpers'
import { sentryConfigureScope } from '@/utils/errorHandler'

export default {
    login({ commit, dispatch }, credentials) {
        return axios
            .post('/users/token', credentials)
            .then(response => {
                const token = response.data.token,
                    userId = decodeUserId(token),
                    userName = decodeUserName(token),
                    userEmail = decodeUserEmail(token),
                    userFirstAndLastName = decodeUserFirstAndLastName(token)

                commit('LOGIN_SUCCESS', {
                    token,
                    userId,
                    userName,
                    userEmail,
                    ...userFirstAndLastName
                })

                localStorage.setItem('token', token)
                localStorage.setItem('userId', userId)
                localStorage.setItem('userName', userName)
                localStorage.setItem('userFirstName', userFirstAndLastName.firstName)
                localStorage.setItem('userLastName', userFirstAndLastName.lastName)
                localStorage.setItem('userEmail', userEmail)

                // Clearing selected data from Store
                dispatch('reports/clearReportSettings')

                sentryConfigureScope()
                axios.defaults.headers.common['Authorization'] = `JWT ${token}`

                return Promise.resolve(response)
            })
            .catch(error => {
                if (typeof error.response === 'undefined' || error.response.status !== 400) {
                    commit('SHOW_NOTIFICATION', {
                        type: 'error',
                        message: 'Wystąpił błąd połączenia.'
                    })
                } else {
                    handleApiError(error)
                }

                return Promise.reject(error)
            })
    },
    logout({ commit }) {
        commit('LOGOUT_SUCCESS')

        localStorage.removeItem('token')
        localStorage.removeItem('userId')
        localStorage.removeItem('userName')
        localStorage.removeItem('userEmail')
        localStorage.removeItem('userPermissions')
        localStorage.removeItem('receivedPermissions')
    },
    getUser({ commit }, userId) {
        return axios
            .get(`/users/users/${userId}`)
            .then(response => {
                commit('GET_USER', response.data)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie danych użytkownika nie powiodło się.')

                return Promise.reject(error)
            })
    },
    changePassword({ commit }, { userId, passwords }) {
        return axios
            .patch(`/users/users/${userId}`, passwords)
            .then(response => {
                commit('SHOW_NOTIFICATION', {
                    type: 'success',
                    message: 'Hasło zostało zmienione.'
                })

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Zmiana hasła nie powiodła się.')

                return Promise.reject(error)
            })
    },
    sendLink({ commit }, payload) {
        return axios
            .post('/users/reset-password', payload)
            .then(response => {
                commit('SHOW_NOTIFICATION', {
                    type: 'success',
                    message: 'Link do zmiany hasła został wysłany na adres e-mail.'
                })

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Wysłanie linku na adres e-mail nie powiodło się.')

                return Promise.reject(error)
            })
    },
    passwordReset({ commit }, payload) {
        return axios
            .post('/users/password-reset-confirmation', payload)
            .then(response => {
                commit('SHOW_NOTIFICATION', {
                    type: 'success',
                    message: 'Hasło zostało zmienione.'
                })

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Zmiana hasła nie powiodła się.')

                return Promise.reject(error)
            })
    },
    refreshToken({ commit }) {
        const payload = {
            token: this.state.user.token
        }

        return axios
            .post('/users/token-refresh', payload)
            .then(response => {
                const token = response.data.token

                commit('REFRESH_TOKEN_SUCCESS', token)

                localStorage.setItem('token', token)

                axios.defaults.headers.common['Authorization'] = `JWT ${token}`

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Twoja sesja wygasła.')

                return Promise.reject(error)
            })
    },
    inspectToken() {
        const token = this.state.user.token,
            TOKEN_REFRESH_EXPIRATION = 86400, // 24h
            TOKEN_CHECKING_PERIOD = 3600 // 1h

        if (token) {
            const decoded = jwt_decode(token),
                exp = decoded.exp,
                orig_iat = decoded.orig_iat,
                now = Date.now() / 1000

            // Jeśli token wygaśnie w przeciągu 1h i cykl życia (24h) nie został przekroczony, odśwież token
            if (
                exp - now < TOKEN_CHECKING_PERIOD &&
                now - orig_iat < (TOKEN_REFRESH_EXPIRATION - TOKEN_CHECKING_PERIOD)
            ) {
                this.dispatch('refreshToken')
                    .catch(() => {})
            }
        }
    },
    getUserPermissions({ commit }) {
        return axios
            .get('/users/permissions')
            .then(response => {
                localStorage.setItem('receivedPermissions', true)
                localStorage.setItem('userPermissions', JSON.stringify(response.data.results))

                commit('GET_USER_PERMISSIONS_SUCCESS', response.data.results)

                return Promise.resolve(response)
            })
            .catch(error => {
                handleApiError(error, 'Pobranie uprawnień użytkownika nie powiodło się.')

                return Promise.reject(error)
            })
    }
}

const decodeUserId = (token) => {
    const decoded = jwt_decode(token)

    return decoded.user_id
}

const decodeUserEmail = (token) => {
    const decoded = jwt_decode(token)

    return decoded.email
}

const decodeUserName = (token) => {
    const decoded = jwt_decode(token)

    return decoded.firstName && decoded.lastName ? `${decoded.firstName} ${decoded.lastName}` : decoded.username
}

const decodeUserFirstAndLastName = (token) => {
    const decoded = jwt_decode(token)

    return {
        firstName: decoded.firstName || null,
        lastName: decoded.lastName || null
    }
}